import { useEffect, useState } from "react"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import Label from "@/v2-ui/label"
import Input from "@/v2-ui/input"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Button from "@/v2-backoffice-client/components/button"
import { useUptimeConfigHostSuffixUpsertMutation } from "@/v2-backoffice-client/uptime/configHostSuffix/upsert/uptime.configHostSuffix.upsert.mutation"
import type { UptimeConfigQuery } from "@/v2-backoffice-client/uptime/config/__types__/uptime.config.query"

type HostSuffix =
  UptimeConfigQuery["getStatusConfig"]["Providers"][number]["HostSuffixes"][number]

export type UptimeConfigHostSuffixDialogProps = DialogProps & {
  mode: "create" | "edit"
  providerId: string
  hostSuffix?: HostSuffix | null
}

const FORM_ID = "uptime-config-host-suffix-form"

function makeInitial(hostSuffix: HostSuffix | null | undefined) {
  return {
    hostSuffix: hostSuffix?.HostSuffix ?? "",
    priority: hostSuffix?.Priority ?? 0,
  }
}

function UptimeConfigHostSuffixDialog(
  props: UptimeConfigHostSuffixDialogProps,
) {
  const { mode, providerId, hostSuffix, open, onOpenChange, ...dialogProps } =
    props

  const [form, setForm] = useState(() => makeInitial(hostSuffix))
  const [upsert, { loading, error, reset }] =
    useUptimeConfigHostSuffixUpsertMutation({
      onCompleted() {
        onOpenChange?.(false)
      },
    })

  useEffect(() => {
    if (open) {
      setForm(makeInitial(hostSuffix))
      reset()
    }
  }, [open, hostSuffix, reset])

  return (
    <Dialog open={open} onOpenChange={onOpenChange} {...dialogProps}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>
            {mode === "edit" ? "Edit host suffix" : "New host suffix"}
          </Dialog.Title>
          <Dialog.Description>
            Probe URLs whose host ends with this suffix resolve to the owning
            provider.
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-3" error={error} />

        <form
          id={FORM_ID}
          className="uptime-config-form"
          onSubmit={(e) => {
            e.preventDefault()
            if (!providerId) return
            upsert({
              variables: {
                input: {
                  providerId,
                  hostSuffix: form.hostSuffix.trim().toLowerCase(),
                  priority: form.priority,
                },
              },
            })
          }}
        >
          <div className="uptime-config-form-field">
            <Label htmlFor="uptime-config-host-suffix-value">Host suffix</Label>
            <Input
              id="uptime-config-host-suffix-value"
              type="text"
              required
              autoFocus
              disabled={mode === "edit"}
              placeholder="e.g. geodataonline.no"
              value={form.hostSuffix}
              onChange={(e) => {
                setForm({ ...form, hostSuffix: e.target.value })
              }}
            />
            <p className="uptime-config-form-hint">
              Match is case-insensitive and treats the value as a suffix
              (`api.example.com` matches `example.com`).
            </p>
          </div>

          <div className="uptime-config-form-field">
            <Label htmlFor="uptime-config-host-suffix-priority">Priority</Label>
            <Input
              id="uptime-config-host-suffix-priority"
              type="number"
              required
              value={form.priority}
              onChange={(e) => {
                setForm({
                  ...form,
                  priority: Number.parseInt(e.target.value, 10) || 0,
                })
              }}
            />
            <p className="uptime-config-form-hint">
              Resolution prefers lower priority and longer suffix on ties.
            </p>
          </div>
        </form>

        <Dialog.Footer>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange?.(false)
            }}
          >
            Cancel
          </Button>
          <Button type="submit" form={FORM_ID} isLoading={loading}>
            {mode === "edit" ? "Save" : "Create"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default UptimeConfigHostSuffixDialog
